import {
  ILibraryExportFile,
  ILibraryImportResponse,
  ILibraryRowsResponse,
  ILibrarySummaryResponse,
  LibraryClaimType,
} from "types/PayerProcedureServiceLine/PayerProcedureServiceLine";

import { api } from ".";

/**
 * CLE-2323 follow-on — RTK Query layer for the per-payer service-line library.
 *
 * Upload-style endpoints (`previewImport`, `import`) accept multipart bodies
 * — same pattern as the splitter / expander mutations. The other endpoints
 * are straightforward JSON GETs / DELETEs.
 */

interface ILibraryUploadRequest {
  file: File;
  payerId: number;
  claimType: LibraryClaimType;
}

interface ILibraryListRequest {
  payerId: number;
  claimType: LibraryClaimType;
  serviceLineName?: string;
  cptCode?: string;
  limit?: number;
  offset?: number;
}

const queries = api.injectEndpoints({
  endpoints: (builder) => ({
    previewLibraryImport: builder.mutation<
      ILibraryImportResponse,
      ILibraryUploadRequest
    >({
      query: ({ file, payerId, claimType }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("payerId", String(payerId));
        formData.append("claimType", claimType);
        return {
          url: "payer-procedure-service-lines/preview-import",
          method: "POST",
          body: formData,
        };
      },
    }),
    importLibrary: builder.mutation<
      ILibraryImportResponse,
      ILibraryUploadRequest
    >({
      query: ({ file, payerId, claimType }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("payerId", String(payerId));
        formData.append("claimType", claimType);
        return {
          url: "payer-procedure-service-lines/import",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["PayerProcedureServiceLines"],
    }),
    getLibrarySummary: builder.query<ILibrarySummaryResponse, void>({
      query: () => "payer-procedure-service-lines/summary",
      providesTags: ["PayerProcedureServiceLines"],
    }),
    getLibraryRows: builder.query<ILibraryRowsResponse, ILibraryListRequest>({
      query: (params) => {
        const qs = new URLSearchParams();
        qs.set("payerId", String(params.payerId));
        qs.set("claimType", params.claimType);
        if (params.serviceLineName)
          qs.set("serviceLineName", params.serviceLineName);
        if (params.cptCode) qs.set("cptCode", params.cptCode);
        if (params.limit !== undefined) qs.set("limit", String(params.limit));
        if (params.offset !== undefined)
          qs.set("offset", String(params.offset));
        return `payer-procedure-service-lines?${qs.toString()}`;
      },
      providesTags: ["PayerProcedureServiceLines"],
    }),
    // CLE-2885 — the stored library as a file. The server decides CSV vs XLSX (following the
    // uploaded file) and says which through the content type. A GET, but declared as a
    // MUTATION on purpose: every call is a fresh request and nothing is cached, so a re-import
    // can never be answered with an old blob. A mutation's `unwrap()` also returns the
    // response itself, where a query's reads it back out of a cache entry that is gone once
    // the row unmounts. No tags either way — the download changes nothing.
    exportLibrary: builder.mutation<
      ILibraryExportFile,
      { payerId: number; claimType: LibraryClaimType }
    >({
      query: ({ payerId, claimType }) => {
        const qs = new URLSearchParams();
        qs.set("payerId", String(payerId));
        qs.set("claimType", claimType);
        return {
          url: `payer-procedure-service-lines/export?${qs.toString()}`,
          method: "GET",
          responseHandler: async (response: Response) => {
            if (!response.ok) {
              try {
                return await response.json();
              } catch {
                return { message: "Could not download the library." };
              }
            }
            return { blob: await response.blob() };
          },
        };
      },
    }),
    deleteLibraryScope: builder.mutation<
      { data: { deletedRows: number }; message: string },
      { payerId: number; claimType: LibraryClaimType }
    >({
      query: ({ payerId, claimType }) => {
        const qs = new URLSearchParams();
        qs.set("payerId", String(payerId));
        qs.set("claimType", claimType);
        return {
          url: `payer-procedure-service-lines?${qs.toString()}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["PayerProcedureServiceLines"],
    }),
  }),
});

export const {
  usePreviewLibraryImportMutation,
  useImportLibraryMutation,
  useGetLibrarySummaryQuery,
  useGetLibraryRowsQuery,
  useExportLibraryMutation,
  useDeleteLibraryScopeMutation,
} = queries;
